<template>
  <div>
    <el-row>
      <el-col :span="12">
        <p>Saved Documents {{ (transaction.number) ? ' for #' + transaction.number : '' }}</p>
      </el-col>
      <el-col v-if="count && count > 20" :span="12">
        <el-row type="flex" justify="end" align="middle">
          <el-pagination
            small
            layout="prev, pager, next"
            :total="count"
            :page-size="20"
            @current-change="changePage"
            :current-page="currentPage">
          </el-pagination>
        </el-row>
      </el-col>
    </el-row>
    <el-row v-loading.body="loading" style="min-height: 150px;" element-loading-text="Loading files">
      <el-table
        striped
        empty-text="No files"
        :data="savedFilesWithReadableDates"
        v-if="savedFiles && savedFiles.length">
        <el-table-column
          prop="original_filename"
          label="File"
          align="left"
          :show-overflow-tooltip="true">
        </el-table-column>
        <el-table-column
          prop="created_on"
          label="Date Uploaded"
          align="center">
        </el-table-column>
        <el-table-column
          fixed="right"
          width="100"
          label="Actions"
          align="center">
          <template v-slot="scope">
            <a :href="scope.row.link" target="_blank"><el-icon class="m-right-5 green"><Picture/></el-icon></a>
            <el-icon class="red delete-btn" @click="deleteAsset(scope.$index, scope.row)"><CircleCloseFilled/></el-icon>
          </template>
        </el-table-column>
      </el-table>
      <div v-else id="nofiles">No files</div>
    </el-row>
    <vue-dropzone
      id="dropzone-transactions"
      ref="dropzone-transactions"
      :options="dzOptions"
      @vdropzone-files-added="handleFileUpload"
      @vdropzone-sending="emitSending"
      @vdropzone-success="emitSuccess"
      @vdropzone-error="emitError">
    </vue-dropzone>
  </div>
</template>

<script>
  import vue2Dropzone from '@/components/VueDropzone.vue'
    import { transactionMixins } from '@/Mixins/Transactions'
  import { DateFormatter } from '@/utils/helpers/FormatDate'

  export default {
    components: {
      vueDropzone: vue2Dropzone
    },
    mixins: [ transactionMixins ],
    props: {
      dzOptions: Object,
      transaction: Object
    },
    data () {
      return {
        savedFiles: [],
        newForm: undefined,
        count: null,
        currentPage: 1,
        url: null,
        loading: false
      }
    },
    methods: {
      /**
       * Changes the current page and calls setUrl to change
       * the url accordingly
       */
      changePage (page) {
        this.currentPage = page
        this.setUrl()
        this.getFiles()
      },
      /**
       * Sets the url
       */
      setUrl () {
        if (this.url) {
          let url = new URL(this.url)
          let queryString = url.search
          if (queryString && queryString.length) {
            let params = new URLSearchParams(queryString)
            params.set('page', this.currentPage)
            url.search = params.toString()
          } else {
            url.searchParams.set('page', this.currentPage)
          }
          this.url = url.toString()
        } else {
          this.url = this.dzOptions.signingUrl
        }
      },
      /**
       * Get list of assets for the current transaction
       */
      getFiles () {
        this.loading = true
        this.$http.get(this.url, { headers: this.dzOptions.signingHeaders })
          .then(resp => {
            this.count = resp.body && resp.body.count ? resp.body.count : 0     // Get total result count (required to show paginator)
            this.savedFiles = resp.body.results.map(file => {
              let row = file
              row.link = this.getAssetDownloadLink(file)
                .then(link => {
                  row.link = link
                  return link
                })
              row.page = this.currentPage
              return row
            })
            this.loading = false
            return this.savedFiles
          })
          .catch(error => {
            this.loading = false
            console.log('Failed to retrieve signed URL')
            console.log(error)
            return false
          })
      },
      /**
       * Call this function when adding to the Dropzone queue to get the AWS signature
       * After first obtaining and setting the signed AWS url and the file's headers
       * and params, this function calls itself recursively, shifting the first file
       * out of the `files` array until all files have been processed.
       * @param files
       * @return {Promise}
       */
      handleFileUpload (files) {
        let dropzone = this.$refs['dropzone-transactions'].dropzone
        let payload = {
          original_filename: files[0].name,
          size_bytes: files[0].size,
          mime_type: files[0].type
        }
        this.$http.post(this.dzOptions.signingUrl, payload, { headers: this.dzOptions.signingHeaders })
          .then(resp => {
            let awsSig = resp.body.aws_signature
            if (!awsSig || typeof awsSig !== 'object' || !awsSig.hasOwnProperty('data')) {
              return Promise.reject('Failed to retrieve signed URL from API')
            }
            this.newForm = new FormData()
            for (let key in awsSig.data.fields) {
              this.newForm.append(key, awsSig.data.fields[key])
            }
            dropzone.options.url = awsSig.data.url
            return this.newForm
          })
          .then(formdata => {
            if (Array.isArray(files) && files.length > 1) {
              files.shift()
              this.handleFileUpload(files)
            } else {
              dropzone.processFile(files[0])
            }
            return formdata
          })
          .catch(e => {
            console.log('Failed to retrieve signed URL')
            console.log(e)
            return e
          })
      },
      /**
       * This catches the file right before sending which means we need to get a signed AWS S3 URL prior to uploading.
       * @param file
       * @param xhr
       * @param formData
       * @return {methods|Component}
       */
      emitSending (file, xhr, formData) {
        for (let [key, value] of this.newForm.entries()) {    // Load our prepared form data into Dropzone's formData
          formData.append(key, value)
        }
        formData.delete('s3ObjectLocation')                   // Delete extraneous keys
        return this.$emit('upload-sending', true)
      },
      /**
       * Reload the list of files
       */
      emitSuccess (file) {
        this.getFiles()
        return this.$emit('upload-success', true)
      },
      emitError (file) {
        this.$emit('upload-error', true)
        return true
      },
      /**
       * Not used
       */
      emitProcessing (file) {
        return this.$emit('upload-processing', true)
      },
      /**
       * Not used
       */
      emitComplete (file) {
        return this.$emit('upload-complete', true)
      },
      /**
       * Emit event when file removed
       */
      emitRemove () {
        this.$emit('upload-remove')
      },
      /**
       * Remove files from Dropzone loader
       */
      removeFiles () {
        this.$refs['dropzone-transactions'].removeAllFiles()
      },
      /**
       * Generate download link for an asset
       * @param file
       * @return {*}
       */
      getAssetDownloadLink (file) {
        if (!file.id) return false
        return new Promise((resolve, reject) => {
          this.$http.get(`${this.dzOptions.instanceUrl}/files/${file.id}/download`, { headers: this.dzOptions.signingHeaders })
            .then(resp => {
              if (resp && resp.body.download_url) return resolve(resp.body.download_url)
              else return reject(false)
            })
            .catch(error => {
              console.log('Failed to retrieve signed URL')
              console.log(error)
              return reject(false)
            })
        })
      },
      /**
       * Delete an asset file
       * @param index
       * @param file
       * @return {boolean}
       */
      deleteAsset (index, file) {
        if (!file.id) return false
        this.createConfirmationBox('Confirm', 'Really Delete this file? ')
          .then(confirmation => {
            if (confirmation === true) {
              this.$http.delete(this.dzOptions.signingUrl + '/' + file.id, { headers: this.dzOptions.signingHeaders })
                .then(resp => {
                  this.getFiles()
                  this.emitRemove()
                  return true
                })
                .catch(error => {
                  console.log('Failed to retrieve signed URL')
                  console.log(error)
                  return false
                })
            } else if (confirmation === false) {
              return false
            }
          })
          .catch(error => {
            console.log(error)
          })
      }
    },
    computed: {
      /**
       * Transforms 'created_on' date from ISO timestamp to YYYY=MM-DD string
       * @return {Array|any[]}
       */
      savedFilesWithReadableDates () {
        return this.savedFiles.map(v => {
          v.created_on = DateFormatter.formatISODateString(v.created_on)
          return v
        })
      }
    },
    created () {
      this.url = this.dzOptions.signingUrl
      this.currentPage = 1
    },
    mounted () {
      this.getFiles()   // Load file list
    },
    // Vue 3: the destroy hook is `beforeUnmount`; `beforeUnload` is not a lifecycle hook and never ran.
    beforeUnmount () {
      this.savedFiles = []
    }
  }
</script>

<style scoped>
  .amount { text-align: right; font-weight: bold; }
  .centered { margin: 0 auto; text-align: center; }
  .subtitle { font-size: 1.5em; margin-bottom: 20px }
  .green { color: #13CE66; }
  .red { color: #FF4949; }
  .lt-blue { color: #58B7FF; }
  .m-right-5 { margin-right: 5px; }
  .delete-btn {  cursor: pointer;  }
  #nofiles {
    margin-top: 40px;
    color: lightgrey;
    text-align: center;
    font-size: 24px;
  }
</style>
